/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/InventoryScan.tsx
 * ----------------------------------------------------------------------------
 * Tela de "Scan / Consulta rápida" no Desktop.
 *
 * O objetivo aqui é manter o comportamento existente:
 *  - Campo de texto para código (SKU/EAN/etc.)
 *  - Opção "Auto (Enter)" para disparar a busca quando o leitor envia ENTER
 *  - Botão "Buscar" para disparo manual
 *
 * E ao mesmo tempo alinhar com o Mobile, reutilizando:
 *  - Hook compartilhado: useStockScan (chama /api/inventory/scan/?q=...)
 *  - Cartão de apresentação: ScanResultCard (descrição, imagens, totais,
 *    saldos por localização, recepção/POs)
 *
 * Vantagens:
 *  - Paridade visual/funcional entre Desktop e Mobile
 *  - Menos código duplicado e mais fácil de evoluir
 * ========================================================================== */

import { useEffect, useState } from 'react'
import { QrCode, Search } from 'lucide-react'
import { useStockScan } from '@/lib/hooks/useStockScan'
import ScanResultCard from '@/components/ScanResultCard'

export default function InventoryScan() {
    // term: o que o usuário está digitando
    // q: termo efetivamente pesquisado (congelado no momento do submit)
    const [term, setTerm] = useState('')
    const [q, setQ] = useState<string>('')
    const [auto, setAuto] = useState(true)

    // Chama o hook compartilhado. "enabled" só liga após termos um q.
    const { data, isFetching, error } = useStockScan(q, !!q)

    // Dispara a busca manualmente (botão ou ENTER no input)
    const submit = () => {
        const v = term.trim()
        if (v) setQ(v)
    }

    // "Auto (Enter)" — mesma ideia do código anterior
    // Fica global para capturar ENTER do leitor Zebra (modo teclado).
    useEffect(() => {
        if (!auto) return
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Enter') submit()
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auto, term])

    return (
        <div className="grid gap-4">
            {/* Cabeçalho / filtros */}
            <div className="card p-4">
                <h2 className="text-lg font-semibold">Scan / Consulta rápida</h2>

                <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
                    <input
                        className="input"
                        placeholder="Digite ou escaneie o código"
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        onKeyDown={(e) => (e.key === 'Enter' ? submit() : null)}
                    />

                    <label className="flex items-center gap-2 px-3 text-sm text-neutral-600 dark:text-neutral-300">
                        <input
                            type="checkbox"
                            checked={auto}
                            onChange={(e) => setAuto(e.target.checked)}
                        />
                        Auto (Enter)
                    </label>

                    <button className="btn" onClick={submit} disabled={!term.trim() || isFetching}>
                        {isFetching ? 'Buscando...' : (
                            <>
                                <Search size={18} /> Buscar
                            </>
                        )}
                    </button>
                </div>

                <div className="mt-2 text-sm text-neutral-500 flex items-center gap-2">
                    <QrCode size={16} /> Use leitor Zebra (modo teclado) ou digite manualmente.
                </div>
            </div>

            {/* Resultado */}
            {/* Reutilizamos o mesmo cartão do Mobile; se quiser suprimir imagens aqui,
          basta passar showImages={false}. */}
            {q ? (
                <ScanResultCard data={data ?? undefined} loading={isFetching} error={error} />
            ) : (
                <div className="card p-4 text-sm text-neutral-500">— Informe um código e clique em Buscar —</div>
            )}

            {/* JSON bruto (útil para debugging)
         - Mantido apenas em desenvolvimento para não aparecer ao usuário final.
         - Caso precise reativar em produção temporariamente, remova a verificação
           `import.meta.env.DEV` abaixo. */}
            {import.meta.env.DEV && q && data && (
                <details className="card p-4">
                    <summary className="cursor-pointer select-none">Ver JSON bruto</summary>
                    <pre className="mt-2 text-xs overflow-auto">{JSON.stringify(data, null, 2)}</pre>
                </details>
            )}
        </div>
    )
}
